import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PhoneIncoming, PhoneOutgoing, PhoneCall, Sparkles, Phone as PhoneIcon, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Softphone } from "../components/Softphone";
import { enablePush, listenForeground, pushPermission } from "../lib/push";

export default function Calls() {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    // dialer
    const [numbers, setNumbers] = useState([]);
    const [fromId, setFromId] = useState("");
    const [toNumber, setToNumber] = useState("");
    const [dialing, setDialing] = useState(false);
    const [pushPerm, setPushPerm] = useState("default");
    const [iosHint, setIosHint] = useState(false);

    const onForeground = (payload) => {
        const data = payload?.data || {};
        const n = payload?.notification || {};
        if (data.type === "incoming_call") {
            toast(`Incoming call${data.from_number ? ` from ${data.from_number}` : ""}`, {
                description: "Tap to open the dialer",
                icon: "📞",
                duration: 15000,
            });
            try { new Audio("/ringtone.mp3").play().catch(() => {}); } catch {}
        } else if (n.title) {
            toast(n.title, { description: n.body });
        }
        load();
    };

    const enableAlerts = async () => {
        const res = await enablePush(onForeground);
        if (res.ok) {
            setPushPerm("granted");
            setIosHint(false);
            toast.success("Call alerts enabled on this device");
        } else if (res.reason === "ios-needs-install") {
            setIosHint(true);
            toast.info("On iPhone, add FlexLine to your Home Screen first to get call alerts.");
        } else if (res.reason === "not-configured") {
            toast.info("Push isn't set up yet — coming soon.");
        } else if (res.reason === "denied") {
            toast.error("Notifications blocked. Enable them in your browser settings.");
        } else if (res.reason === "unsupported") {
            toast.error("This browser doesn't support push notifications.");
        } else {
            toast.error("Couldn't enable alerts. Please try again.");
        }
    };

    const load = async () => {
        const { data } = await api.get("/calls");
        setCalls(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        setPushPerm(pushPermission());
        (async () => {
            try {
                const { data } = await api.get("/numbers/my");
                setNumbers(data);
                if (data.length > 0) setFromId(data[0].number_id);
            } catch {}
        })();
        // If already granted, wire the foreground listener silently
        if (pushPermission() === "granted") {
            listenForeground(onForeground).catch(() => {});
        }
    }, []);

    const addDemo = async () => {
        setSeeding(true);
        try {
            await api.post("/calls/demo");
            toast.success("Demo call added");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setSeeding(false);
        }
    };

    const dial = async (e) => {
        e?.preventDefault();
        if (!fromId || !toNumber) {
            toast.error("Pick a from number and enter a destination");
            return;
        }
        setDialing(true);
        try {
            await api.post("/calls/start", { from_number_id: fromId, to_number: toNumber });
            toast.success("Call placed");
            setToNumber("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Call failed");
        } finally {
            setDialing(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="calls-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Voice</p>
                    <h1 className="font-display text-4xl font-light tracking-tight">
                        Call <span className="font-serif-italic">anywhere.</span>
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={enableAlerts}
                        disabled={pushPerm === "granted"}
                        data-testid="enable-call-alerts-btn"
                        variant={pushPerm === "granted" ? "default" : "outline"}
                        className={pushPerm === "granted"
                            ? "rounded-sm h-10 bg-brand-green text-[#FAF5FF] border border-brand-green hover:bg-brand-green disabled:opacity-100"
                            : "rounded-sm border-[#DDD6FE] h-10"}
                    >
                        {pushPerm === "granted"
                            ? <><BellRing className="w-4 h-4 mr-2" /> Alerts on</>
                            : <><Bell className="w-4 h-4 mr-2" /> Enable call alerts</>}
                    </Button>
                    <Button
                        onClick={addDemo}
                        disabled={seeding}
                        data-testid="add-demo-call-btn"
                        variant="outline"
                        className="rounded-sm border-[#DDD6FE] h-10"
                    >
                        <Sparkles className="w-4 h-4 mr-2" /> Add demo
                    </Button>
                </div>
            </div>

            {/* In-app calling (WebRTC) for FlexLine Mobile numbers */}
            <Softphone />

            {iosHint && (
                <div className="border border-[#DDD6FE] rounded-sm bg-cream/40 p-6" data-testid="ios-install-hint">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">— Turn on call alerts on iPhone</p>
                    <p className="text-sm text-zinc-700 mb-3">
                        Apple only allows call/notification alerts when FlexLine is added to your Home Screen. It takes 10 seconds:
                    </p>
                    <ol className="text-sm text-zinc-700 space-y-1 list-decimal list-inside">
                        <li>In Safari, tap the <span className="font-semibold">Share</span> button (the square with an ↑ arrow).</li>
                        <li>Choose <span className="font-semibold">"Add to Home Screen"</span>, then tap <span className="font-semibold">Add</span>.</li>
                        <li>Open <span className="font-semibold">FlexLine</span> from the new Home Screen icon.</li>
                        <li>Come back to this Calls page and tap <span className="font-semibold">"Enable call alerts"</span> → allow notifications.</li>
                    </ol>
                    <p className="text-xs text-zinc-500 mt-3">Requires iOS 16.4 or later.</p>
                </div>
            )}

            {/* Dialer */}
            {numbers.length > 0 && (
                <form onSubmit={dial} className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="dialer">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-4">— Place a call</p>
                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-4">
                            <label className="font-mono text-[10px] uppercase text-zinc-500">From</label>
                            <select
                                value={fromId}
                                onChange={(e) => setFromId(e.target.value)}
                                data-testid="dialer-from-select"
                                className="w-full mt-1 h-10 px-3 rounded-sm border border-[#DDD6FE] font-mono text-sm bg-cream/40"
                            >
                                {numbers.map((n) => (
                                    <option key={n.number_id} value={n.number_id}>{n.phone_number}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-6">
                            <label className="font-mono text-[10px] uppercase text-zinc-500">To</label>
                            <Input
                                value={toNumber}
                                onChange={(e) => setToNumber(e.target.value)}
                                placeholder="+15551234567"
                                className="rounded-sm font-mono mt-1"
                                data-testid="dialer-to-input"
                            />
                        </div>
                        <div className="md:col-span-2 flex items-end">
                            <Button
                                type="submit"
                                disabled={dialing}
                                data-testid="dialer-call-btn"
                                className="w-full bg-emerald-deep text-[#FAF5FF] rounded-sm h-10"
                            >
                                <PhoneIcon className="w-4 h-4 mr-1" /> Call
                            </Button>
                        </div>
                    </div>
                </form>
            )}

            {loading && <div className="text-sm text-zinc-500">Loading…</div>}

            {!loading && calls.length === 0 && (
                <div className="border border-zinc-200 rounded-sm p-12 text-center bg-zinc-50/50">
                    <PhoneCall className="w-6 h-6 mx-auto text-accent-blue mb-3" />
                    <p className="font-display text-xl font-bold">No calls yet</p>
                    <p className="text-zinc-600 mt-2">Once your numbers receive calls, they will show up here.</p>
                </div>
            )}

            {calls.length > 0 && (
                <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-zinc-200 bg-zinc-50/50">
                                <Th>Type</Th>
                                <Th>Number</Th>
                                <Th>Status</Th>
                                <Th>Duration</Th>
                                <Th>When</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {calls.map((c) => (
                                <tr key={c.call_id} className="border-b border-zinc-100 hover:bg-zinc-50/40" data-testid={`call-${c.call_id}`}>
                                    <Td>
                                        <span className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase ${
                                            c.direction === "inbound" ? "text-accent-blue" : "text-zinc-500"
                                        }`}>
                                            {c.direction === "inbound" ? <PhoneIncoming className="w-3 h-3" /> : <PhoneOutgoing className="w-3 h-3" />}
                                            {c.direction}
                                        </span>
                                    </Td>
                                    <Td><span className="font-mono">{c.direction === "inbound" ? c.from_number : c.to_number}</span></Td>
                                    <Td><span className="font-mono text-xs">{c.status}</span></Td>
                                    <Td><span className="font-mono text-xs">{Math.floor(c.duration_seconds / 60)}m {c.duration_seconds % 60}s</span></Td>
                                    <Td><span className="font-mono text-xs text-zinc-500">{new Date(c.created_at).toLocaleString()}</span></Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
const Th = ({ children }) => <th className="text-left px-5 py-3 font-mono text-[10px] uppercase text-zinc-500">{children}</th>;
const Td = ({ children }) => <td className="px-5 py-3 text-sm">{children}</td>;
